import { useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { SEARCH_BY_CHECKED_STATE, SEARCH_DEBOUNCE_DELAY } from '../configs'
import { assignChildToParent } from '../helpers/assignChildToParent'
import { getTreeStructure } from '../helpers/getTreeStructure'
import { mapDataShapeToTreeStructure } from '../helpers/mapDataShapeToTreeStructure'
import { IDataShape } from '../models/dataShape'
import { IUseTreeDataParameters, IUseTreeDataResult } from '../models/iUseTreeData'

import { useSelectedValues } from './useSelectedValues'

export const useTreeData = <T extends IDataShape>({
  data,
  values,
  itemCaption,
  onSearch,
  typeOfSelectedNodes,
}: IUseTreeDataParameters<T>): IUseTreeDataResult => {
  const [searchValue, setSearchValue] = useState('')
  const [showOnlySelected, setShowOnlySelected] = useState(false)

  const flatData = useMemo(() => {
    const mappedData = mapDataShapeToTreeStructure(data, { itemCaption })
    return assignChildToParent(mappedData)
  }, [data, itemCaption])
  const [selectedValues] = useSelectedValues({
    values,
    flatData,
    typeOfSelectedNodes,
  })

  const filteredTreeData = useMemo(() => {
    const filteredData = data
      .filter((it) => {
        if (!searchValue.length) {
          return true
        }

        if (onSearch) {
          return onSearch(it, searchValue)
        }

        return `${it[itemCaption]}`.toLowerCase().includes(searchValue.toLowerCase())
      })
      .filter((it) => {
        if (!showOnlySelected) {
          return true
        }

        return selectedValues.some(
          (selected) =>
            it.itemId === selected.itemId && SEARCH_BY_CHECKED_STATE.includes(selected.checkedState)
        )
      })

    return getTreeStructure(filteredData, { itemCaption })
  }, [data, itemCaption, searchValue, showOnlySelected, selectedValues, onSearch])

  const [debounced] = useDebounce(filteredTreeData, SEARCH_DEBOUNCE_DELAY)

  return {
    flatData,
    treeData: debounced,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
  }
}
